import {NexthinkActionWidgetMeta} from "@me8eon/ai.tools.nextthink/src/model/nexthinkWidget";
import {WidgetComponent} from "../widget.types";
import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useContext,
} from "react";
import { EonUiButton, EonUiDropdown, EonUiDropdownOption } from "@eon-ui/eon-ui-components-react";
import LoaderComponent from "../../../components/loader-component/LoaderComponent";
import { AiAssistantContext } from "../../../context/AiAssistantContext";
import TypingEffect from "../../../components/ai-assist-refactored/utils/typing-effect/TypingEffect";
import {
    processRemoteAction,
    fetchTriggerStatus,
} from "@me8eon/ai.tools.nextthink";
import { getCurrentBrowser, BROWSER_CHROME, BROWSER_EDGE } from "@me8eon/browser";
import { nexthinkRemoteActions, failureMsg } from "../../../components/ai-assist-refactored/action-components/nexthinkActions";
import DeviceSelectionComponent from "../../../components/ai-assist-refactored/action-components/device.selection.component";
import "./nexthinkActions.component.scss";
import {useJwtToken} from "@me8eon/react_authentication";
import {useDoAi} from "@me8eon/react_intent";
import { Icon } from "@me8eon/icons";
import { useLocalTranslation } from "./useLocalTranslation";

const CACHE_ACTION_CATEGORY_BY_BROWSER: Record<string, string> = {
    [BROWSER_CHROME]: "chrome-cache",
    [BROWSER_EDGE]: "edge-cache",
    chrome: "chrome-cache",
    edge: "edge-cache",
};

export const NextthinkActionWidget: WidgetComponent = (props) => {
    const values = props.values._meta.widget as NexthinkActionWidgetMeta;
    const { devices, document, queryLanguage } = values.props;
    const jwtTokenFn = useJwtToken();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [responseData, setResponseData] = useState<any>(null);
    const [deviceId, setDeviceId] = useState<string | null>(
        devices?.length
            ? devices[0]["device.collector.uid"]
            : null
    );
    const [polling, setPolling] = useState<
        "PROGRESS" | "COMPLETED" | "FAILED" | null
    >(null);
    const { addAssistantTask } = useContext(AiAssistantContext);
    const supportedCacheBrowsers = [BROWSER_CHROME, BROWSER_EDGE];
    const browser = useMemo(() => getCurrentBrowser(), []);
    const isCacheAction = document.category === "chrome-cache";
    const isSupportedBrowser = supportedCacheBrowsers.includes(browser);
    const [selectedCacheActionCategory, setSelectedCacheActionCategory] = useState<string>(
        isSupportedBrowser
            ? CACHE_ACTION_CATEGORY_BY_BROWSER[browser] ?? "chrome-cache"
            : "chrome-cache"
    );
    const handleAiQuery = useDoAi();

    useEffect(() => {
        setLoading(false);
        setError(false);
        setResponseData(null);
        setPolling(null);
    }, [document.category]);

    const currentActionCategory = useMemo(() => {
        if (isCacheAction) {
            return selectedCacheActionCategory;
        }
        return document.category;
    }, [document.category, isCacheAction, selectedCacheActionCategory]);

    useEffect(() => {
        if (document.category === "") {
            setError(true);
        }
    }, [document.category]);

    const { localT } = useLocalTranslation(queryLanguage);
    const currentAction = useMemo(
        () =>
            nexthinkRemoteActions[
            currentActionCategory as keyof typeof nexthinkRemoteActions
            ],
        [currentActionCategory]
    );

    const triggerAction = useCallback(async () => {
        if (!currentAction.actionId) return;
        setLoading(true);
        const resp = await processRemoteAction(
            currentAction.actionId,
            deviceId as string,
            jwtTokenFn
        );
        if (!resp) {
            setError(true);
            setLoading(false);
        } else {
            addAssistantTask({
                requestId: resp.requestId,
                task: currentAction.actionId,
                status: "progress",
                timestamp: new Date().getTime(),
            });
            setResponseData(resp);
        }
    }, [currentAction, deviceId]);

    useEffect(() => {
        if (responseData?.requestId) {
            fetchStatus(responseData.requestId);
        }
    }, [responseData]);

    const fetchStatus = useCallback((requestId: string) => {
        setLoading(false);
        setPolling("PROGRESS");
        const interval = setInterval(async () => {
            const { data } = await fetchTriggerStatus(requestId, jwtTokenFn);
            if (data.length && data[0]["remote_action.execution.status"]) {
                const status = data[0]["remote_action.execution.status"];
                if (["success", "failure"].includes(status)) {
                    setPolling(status === "success" ? "COMPLETED" : "FAILED");
                    addAssistantTask({
                        requestId: requestId,
                        task: currentAction.actionId,
                        status: status,
                    });
                    clearInterval(interval);
                }
            }
        }, 5000);

        return () => clearInterval(interval); // Cleanup on unmount
    }, [currentAction]);

    const ActionSection = useMemo(
        () => (
            <div className="bot-reply-container">
                {error ? (
                    <span>{localT(failureMsg)}</span>
                ) : (
                    <>
                        <div>
                            <div className="automated-fix-label">
                                <Icon name="tick_small" themedColor="#00B876"/>
                                <span className="title">{localT("chatBot.nexthinkIntegration.automatedFix")}</span>
                            </div>
                            <div className="nexthink-action-disclaimer">{localT("chatBot.nexthinkIntegration.automatedFixConsent")}</div>
                        </div>
                        {devices.length > 1 && <DeviceSelectionComponent
                            value={deviceId as string}
                            options={devices.map((device: any) => ({
                                value: device["device.collector.uid"],
                                label: device["device.name"],
                            }))}
                            changeHandler={setDeviceId}
                            t={t}
                        />}
                        {isCacheAction && (
                            <div className="cache-browser-selector">
                                <span className="note">{localT("chatBot.nexthinkIntegration.chooseBrowser")}</span>
                                <EonUiDropdown
                                    onValueChanged={(event: any) => setSelectedCacheActionCategory(event.target.value)}
                                    value={selectedCacheActionCategory}
                                    name="browser-dropdown"
                                    size="small"
                                >
                                    <EonUiDropdownOption value="chrome-cache" label="Google Chrome" />
                                    <EonUiDropdownOption value="edge-cache" label="Microsoft Edge" />
                                </EonUiDropdown>
                            </div>
                        )}
                        {loading && (
                            <LoaderComponent isLoading={loading} isLinear />
                        )}
                        {!responseData && (
                            <div className="ai-action-btn-wrapper">
                                <EonUiButton
                                    fullWidthOnMobile
                                    text={localT("chatBot.nexthinkIntegration.primaryButton")}
                                    rank="primary"
                                    size="small"
                                    onClick={triggerAction}
                                    disabled={loading}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        ),
        [
            document.category,
            currentAction,
            loading,
            error,
            responseData,
            triggerAction,
            localT,
            isCacheAction,
            selectedCacheActionCategory,
        ]
    );

    const PollingStatus = useMemo(() => {
        if (polling === null) return null;
        const statusMsg =
            polling === "PROGRESS"
                ? localT(currentAction.taskProgressMsg)
                : polling === "COMPLETED"
                    ? `${localT(currentAction.successMsg)}`
                    : localT(currentAction.failureMsg);
        return (
            <div className="bot-header">
                <div className="bot-reply-container">
                    <div className="title"> <LoaderComponent isLoading={polling === "PROGRESS"} isInline size="small" />{statusMsg}</div>
                </div>
            </div>
        );
    }, [polling, currentAction, localT]);

    return (
        <div 
            className="ai-assist-wrapper"
            style={{
                flexDirection: "column",
                minHeight: "auto",
                padding: 0,
                margin: 0,
                gap: 0,
            }}
        >
            <div className="bot-header" style={{padding:0, gap:0}}>
                {ActionSection}
            </div>
            {PollingStatus}
        </div>
    );
}
